/** @module wgfractals/util/pipeline */
import { getDevice } from "../init.mjs";
import { shaderSources } from "../shaders/registry.mjs";

// Per-device pipeline cache. Every formula shader always pairs with the same
// three shared modules (see SHARED_MODULES below), so compilation just
// concatenates them ahead of the formula-specific source.

const SHARED_MODULES = ["complex", "viewport", "palette"];

// f64 formulas need these too, in dependency order (see f64/complex.wgsl's header).
const F64_MODULES = [
  "f64/dekker",
  "f64/utils/add",
  "f64/utils/multiply",
  "f64/utils/subtract",
  "f64/utils/greater",
  "f64/complex",
  "f64/viewport",
];

// Shaders needing more than the default three shared modules.
const EXTRA_MODULES_BY_SHADER = {
  "f64/mandelbrot": F64_MODULES,
};

const _pipelines = new WeakMap();

/**
 * Returns a cached `GPUComputePipeline` for a fractal formula, compiling it
 * on first use. Pipelines are cached per device so switching fractals at
 * runtime after the first render is just a cache lookup, not a recompile.
 * @param {GPUDevice} device
 * @param {string} shaderName - key into `shaderSources` (e.g. "mandelbrot")
 * @returns {Promise<GPUComputePipeline>}
 */
export async function getPipeline(device, shaderName) {
  if (!_pipelines.has(device)) {
    _pipelines.set(device, new Map());
  }
  const byName = _pipelines.get(device);
  if (!byName.has(shaderName)) {
    byName.set(shaderName, await compilePipeline(shaderName));
  }
  return byName.get(shaderName);
}

/**
 * Compiles the shared modules concatenated with the named formula shader
 * into a `GPUComputePipeline`. Throws with line-level detail if compilation fails.
 * @param {string} shaderName
 * @returns {Promise<GPUComputePipeline>}
 * @throws {Error} if the shader is unknown or has compilation errors
 */
async function compilePipeline(shaderName) {
  const device = getDevice();
  const src = shaderSources[shaderName];
  if (!src) {
    throw new Error(`Shader "${shaderName}" not found in registry.`);
  }

  const extraModules = EXTRA_MODULES_BY_SHADER[shaderName] ?? [];
  const code = [
    ...SHARED_MODULES.map((name) => shaderSources[name]),
    ...extraModules.map((name) => shaderSources[name]),
    src,
  ].join("\n");
  const shaderModule = device.createShaderModule({ label: shaderName, code });

  const info = await shaderModule.getCompilationInfo();
  const errors = info.messages.filter((m) => m.type === "error");
  if (errors.length > 0) {
    throw new Error(
      `Shader "${shaderName}" compilation failed:\n${errors.map((m) => `  line ${m.lineNum}: ${m.message}`).join("\n")}`,
    );
  }

  const pipeline = device.createComputePipeline({
    label: shaderName,
    layout: "auto",
    compute: { module: shaderModule },
  });

  pipeline._shaderModule = shaderModule; // anchor — GC'd shaderModule crashes native pipeline

  return pipeline;
}
